/**
 * Google Analytics 4 E-commerce Tracking Utilities
 * GA4 Enhanced Ecommerce event'lerini gönderen yardımcı fonksiyonlar
 */

// gtag fonksiyonunun tanımlı olup olmadığını kontrol et
const isGtagAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Ürün objesini GA4 formatına çevirir
 * @param {Object} product - Ürün bilgileri
 * @param {number} quantity - Ürün adedi
 * @param {string} variant - Ürün varyantı (beden, renk vb.)
 * @returns {Object} GA4 formatında ürün objesi
 */
const formatProductForGA4 = (product, quantity = 1, variant = null) => {
  return {
    item_id: product.id || product.product_id,
    item_name: product.name || product.title,
    affiliation: 'Minela Kids',
    currency: 'TRY',
    discount: product.discount_amount || 0,
    item_brand: 'Minela Kids',
    item_category: product.category_name || product.category || '',
    item_variant: variant || product.selected_size || '',
    price: parseFloat(product.price || product.final_price || 0),
    quantity: quantity
  };
};

/**
 * View Item - Ürün detay sayfası görüntüleme
 * @param {Object} product - Ürün bilgileri
 */
export const trackViewItem = (product) => {
  if (!isGtagAvailable() || !product) return;

  try {
    window.gtag('event', 'view_item', {
      currency: 'TRY',
      value: parseFloat(product.price || product.final_price || 0),
      items: [formatProductForGA4(product)]
    });
    console.log('GA4: view_item tracked', product.name);
  } catch (error) {
    console.error('GA4 view_item error:', error);
  }
};

/**
 * Add to Cart - Sepete ekleme
 * @param {Object} product - Ürün bilgileri
 * @param {number} quantity - Ürün adedi
 * @param {string} variant - Seçilen varyant (beden vb.)
 */
export const trackAddToCart = (product, quantity = 1, variant = null) => {
  if (!isGtagAvailable() || !product) return;

  try {
    const value = parseFloat(product.price || product.final_price || 0) * quantity;
    
    window.gtag('event', 'add_to_cart', {
      currency: 'TRY',
      value: value,
      items: [formatProductForGA4(product, quantity, variant)]
    });
    console.log('GA4: add_to_cart tracked', product.name, quantity, variant);
  } catch (error) {
    console.error('GA4 add_to_cart error:', error);
  }
};

/**
 * Remove from Cart - Sepetten çıkarma
 * @param {Object} product - Ürün bilgileri
 * @param {number} quantity - Çıkarılan ürün adedi
 */
export const trackRemoveFromCart = (product, quantity = 1) => {
  if (!isGtagAvailable() || !product) return;

  try {
    const value = parseFloat(product.price || product.final_price || 0) * quantity;
    
    window.gtag('event', 'remove_from_cart', {
      currency: 'TRY',
      value: value,
      items: [formatProductForGA4(product, quantity)]
    });
    console.log('GA4: remove_from_cart tracked', product.name);
  } catch (error) {
    console.error('GA4 remove_from_cart error:', error);
  }
};

/**
 * View Cart - Sepet sayfası görüntüleme
 * @param {Array} cartItems - Sepetteki ürünler
 */
export const trackViewCart = (cartItems) => {
  if (!isGtagAvailable() || !cartItems || cartItems.length === 0) return;

  try {
    const items = cartItems.map(item => 
      formatProductForGA4(item.product || item, item.quantity, item.selected_size)
    );
    
    const totalValue = cartItems.reduce((sum, item) => {
      const price = parseFloat(item.product?.price || item.price || 0);
      return sum + (price * item.quantity);
    }, 0);

    window.gtag('event', 'view_cart', {
      currency: 'TRY',
      value: totalValue,
      items: items
    });
    console.log('GA4: view_cart tracked', cartItems.length, 'items');
  } catch (error) {
    console.error('GA4 view_cart error:', error);
  }
};

/**
 * Begin Checkout - Ödeme sürecine başlama
 * @param {Array} cartItems - Sepetteki ürünler
 * @param {number} totalValue - Toplam tutar
 */
export const trackBeginCheckout = (cartItems, totalValue) => {
  if (!isGtagAvailable() || !cartItems || cartItems.length === 0) return;

  try {
    const items = cartItems.map(item => 
      formatProductForGA4(item.product || item, item.quantity, item.selected_size)
    );

    window.gtag('event', 'begin_checkout', {
      currency: 'TRY',
      value: parseFloat(totalValue || 0),
      items: items
    });
    console.log('GA4: begin_checkout tracked', totalValue);
  } catch (error) {
    console.error('GA4 begin_checkout error:', error);
  }
};

/**
 * Add Shipping Info - Kargo bilgisi ekleme
 * @param {Array} cartItems - Sepetteki ürünler
 * @param {number} totalValue - Toplam tutar
 * @param {string} shippingTier - Kargo tipi (örn: 'Standard', 'Express')
 */
export const trackAddShippingInfo = (cartItems, totalValue, shippingTier = 'Standard') => {
  if (!isGtagAvailable() || !cartItems || cartItems.length === 0) return;

  try {
    const items = cartItems.map(item => 
      formatProductForGA4(item.product || item, item.quantity, item.selected_size)
    );

    window.gtag('event', 'add_shipping_info', {
      currency: 'TRY',
      value: parseFloat(totalValue || 0),
      shipping_tier: shippingTier,
      items: items
    });
    console.log('GA4: add_shipping_info tracked', shippingTier);
  } catch (error) {
    console.error('GA4 add_shipping_info error:', error);
  }
};

/**
 * Add Payment Info - Ödeme bilgisi ekleme
 * @param {Array} cartItems - Sepetteki ürünler
 * @param {number} totalValue - Toplam tutar
 * @param {string} paymentType - Ödeme tipi (örn: 'credit_card', 'bank_transfer')
 */
export const trackAddPaymentInfo = (cartItems, totalValue, paymentType) => {
  if (!isGtagAvailable() || !cartItems || cartItems.length === 0) return;

  try {
    const items = cartItems.map(item => 
      formatProductForGA4(item.product || item, item.quantity, item.selected_size)
    );

    window.gtag('event', 'add_payment_info', {
      currency: 'TRY',
      value: parseFloat(totalValue || 0),
      payment_type: paymentType,
      items: items
    });
    console.log('GA4: add_payment_info tracked', paymentType);
  } catch (error) {
    console.error('GA4 add_payment_info error:', error);
  }
};

/**
 * Purchase - Satın alma tamamlama
 * @param {Object} orderData - Sipariş bilgileri
 */
export const trackPurchase = (orderData) => {
  if (!isGtagAvailable() || !orderData) return;

  try {
    const items = (orderData.items || orderData.order_items || []).map(item => 
      formatProductForGA4(item.product || item, item.quantity, item.selected_size)
    );

    window.gtag('event', 'purchase', {
      transaction_id: orderData.order_id || orderData.id,
      affiliation: 'Minela Kids',
      currency: 'TRY',
      value: parseFloat(orderData.total_amount || orderData.total || 0),
      tax: parseFloat(orderData.tax_amount || 0),
      shipping: parseFloat(orderData.shipping_cost || 0),
      items: items
    });
    console.log('GA4: purchase tracked', orderData.order_id);
  } catch (error) {
    console.error('GA4 purchase error:', error);
  }
};

/**
 * View Item List - Ürün listesi görüntüleme
 * @param {Array} products - Ürün listesi
 * @param {string} listName - Liste adı (örn: 'Search Results', 'Category: Bebek')
 */
export const trackViewItemList = (products, listName = 'Product List') => {
  if (!isGtagAvailable() || !products || products.length === 0) return;

  try {
    const items = products.slice(0, 20).map((product, index) => ({
      ...formatProductForGA4(product),
      item_list_name: listName,
      item_list_id: listName.toLowerCase().replace(/\s+/g, '_'),
      index: index
    }));

    window.gtag('event', 'view_item_list', {
      item_list_name: listName,
      item_list_id: listName.toLowerCase().replace(/\s+/g, '_'),
      items: items
    });
    console.log('GA4: view_item_list tracked', listName, products.length);
  } catch (error) {
    console.error('GA4 view_item_list error:', error);
  }
};

/**
 * Select Item - Ürün listesinden seçim
 * @param {Object} product - Seçilen ürün
 * @param {string} listName - Liste adı
 * @param {number} index - Ürünün listedeki sırası
 */
export const trackSelectItem = (product, listName = 'Product List', index = 0) => {
  if (!isGtagAvailable() || !product) return;

  try {
    window.gtag('event', 'select_item', {
      item_list_name: listName,
      item_list_id: listName.toLowerCase().replace(/\s+/g, '_'),
      items: [{
        ...formatProductForGA4(product),
        item_list_name: listName,
        index: index
      }]
    });
    console.log('GA4: select_item tracked', product.name);
  } catch (error) {
    console.error('GA4 select_item error:', error);
  }
};

/**
 * Add to Wishlist - Favorilere ekleme
 * @param {Object} product - Ürün bilgileri
 */
export const trackAddToWishlist = (product) => {
  if (!isGtagAvailable() || !product) return;

  try {
    window.gtag('event', 'add_to_wishlist', {
      currency: 'TRY',
      value: parseFloat(product.price || product.final_price || 0),
      items: [formatProductForGA4(product)]
    });
    console.log('GA4: add_to_wishlist tracked', product.name);
  } catch (error) {
    console.error('GA4 add_to_wishlist error:', error);
  }
};

/**
 * Remove from Wishlist - Favorilerden çıkarma
 * @param {Object} product - Ürün bilgileri
 */
export const trackRemoveFromWishlist = (product) => {
  if (!isGtagAvailable() || !product) return;

  try {
    window.gtag('event', 'remove_from_wishlist', {
      currency: 'TRY',
      value: parseFloat(product.price || product.final_price || 0),
      items: [formatProductForGA4(product)]
    });
    console.log('GA4: remove_from_wishlist tracked', product.name);
  } catch (error) {
    console.error('GA4 remove_from_wishlist error:', error);
  }
};

/**
 * Search - Arama yapma
 * @param {string} searchTerm - Arama terimi
 */
export const trackSearch = (searchTerm) => {
  if (!isGtagAvailable() || !searchTerm) return;

  try {
    window.gtag('event', 'search', {
      search_term: searchTerm
    });
    console.log('GA4: search tracked', searchTerm);
  } catch (error) {
    console.error('GA4 search error:', error);
  }
};

/**
 * View Promotion - Kampanya görüntüleme
 * @param {Object} promotion - Kampanya bilgileri
 */
export const trackViewPromotion = (promotion) => {
  if (!isGtagAvailable() || !promotion) return;

  try {
    window.gtag('event', 'view_promotion', {
      creative_name: promotion.name || promotion.title,
      creative_slot: promotion.position || 'homepage',
      promotion_id: promotion.id,
      promotion_name: promotion.name || promotion.title
    });
    console.log('GA4: view_promotion tracked', promotion.name);
  } catch (error) {
    console.error('GA4 view_promotion error:', error);
  }
};

/**
 * Select Promotion - Kampanyaya tıklama
 * @param {Object} promotion - Kampanya bilgileri
 */
export const trackSelectPromotion = (promotion) => {
  if (!isGtagAvailable() || !promotion) return;

  try {
    window.gtag('event', 'select_promotion', {
      creative_name: promotion.name || promotion.title,
      creative_slot: promotion.position || 'homepage',
      promotion_id: promotion.id,
      promotion_name: promotion.name || promotion.title
    });
    console.log('GA4: select_promotion tracked', promotion.name);
  } catch (error) {
    console.error('GA4 select_promotion error:', error);
  }
};

/**
 * Refund - İade işlemi
 * @param {Object} refundData - İade bilgileri
 */
export const trackRefund = (refundData) => {
  if (!isGtagAvailable() || !refundData) return;

  try {
    const items = (refundData.items || []).map(item => 
      formatProductForGA4(item.product || item, item.quantity)
    );

    window.gtag('event', 'refund', {
      transaction_id: refundData.order_id,
      currency: 'TRY',
      value: parseFloat(refundData.refund_amount || 0),
      items: items
    });
    console.log('GA4: refund tracked', refundData.order_id);
  } catch (error) {
    console.error('GA4 refund error:', error);
  }
};

// Custom Events

/**
 * Share - Paylaşım
 * @param {string} method - Paylaşım metodu (facebook, twitter, vb.)
 * @param {string} contentType - İçerik tipi
 * @param {string} itemId - İçerik ID
 */
export const trackShare = (method, contentType, itemId) => {
  if (!isGtagAvailable()) return;

  try {
    window.gtag('event', 'share', {
      method: method,
      content_type: contentType,
      item_id: itemId
    });
    console.log('GA4: share tracked', method, contentType);
  } catch (error) {
    console.error('GA4 share error:', error);
  }
};

/**
 * Filter Applied - Filtre uygulandı
 * @param {string} filterType - Filtre tipi (price, category, size, vb.)
 * @param {string} filterValue - Filtre değeri
 */
export const trackFilterApplied = (filterType, filterValue) => {
  if (!isGtagAvailable()) return;

  try {
    window.gtag('event', 'filter_applied', {
      filter_type: filterType,
      filter_value: filterValue
    });
    console.log('GA4: filter_applied tracked', filterType, filterValue);
  } catch (error) {
    console.error('GA4 filter_applied error:', error);
  }
};

/**
 * Sort Changed - Sıralama değişti
 * @param {string} sortType - Sıralama tipi
 */
export const trackSortChanged = (sortType) => {
  if (!isGtagAvailable()) return;

  try {
    window.gtag('event', 'sort_changed', {
      sort_type: sortType
    });
    console.log('GA4: sort_changed tracked', sortType);
  } catch (error) {
    console.error('GA4 sort_changed error:', error);
  }
};

export default {
  trackViewItem,
  trackAddToCart,
  trackRemoveFromCart,
  trackViewCart,
  trackBeginCheckout,
  trackAddShippingInfo,
  trackAddPaymentInfo,
  trackPurchase,
  trackViewItemList,
  trackSelectItem,
  trackAddToWishlist,
  trackRemoveFromWishlist,
  trackSearch,
  trackViewPromotion,
  trackSelectPromotion,
  trackRefund,
  trackShare,
  trackFilterApplied,
  trackSortChanged
};
